import * as _ from "lodash";
import * as angular from "angular";
import { ElectionDataProvider } from "../../data-provider/election.dp";
import { FieldDataProvider } from "../../data-provider/field.dp";
import { VoterFileDataProvider } from "../../data-provider/voter-file.dp";
import { PermissionCheck } from "../../shared/permission";
import { Field } from "../../models/field.model";
import { VoterFileImport } from "../../models/voter-file-import.model";
import { DialogUtils } from "../../helper/dialog.helper";
import { ScreenUtils } from "../../helper/screen.helper";
import { ToastUtils } from "../../helper/toast.helper";
import { Utils } from "../../helper/utils.helper";
import { NavigationBarHelperService } from "../navigation/navigation-bar.helper";

let VoterFileImportComponent = {
    selector: "vcVoterFileImport",
    templateUrl:
        "/angular/components/voter-file/voter-file-import.component.html",
    bindings: {},
    controller: class VoterFileImportController {
        private readonly $scope: any;
        private $routeParams: any;
        private dataProvider: VoterFileDataProvider;
        private readonly toast: ToastUtils;
        private fieldDataProvider: FieldDataProvider;
        private $window: any;
        private electionDataProvider: ElectionDataProvider;
        public readonly __env: any;
        private readonly __app_type: string;
        private readonly dialog: DialogUtils;
        private utils: Utils;
        private readonly screen: ScreenUtils;

        public error: string;
        public register: any = {};
        public validVoterFile = true;
        public readonly voterFileString: string;
        public isLeaderView: boolean;
        public matchedCount = 0;
        public registerColumns: any[] = [];
        public readonly defaultColumn = {
            columnid: null,
            columnname: null,
            cfid: null,
            cfname: null,
            elid: null,
            elname: null,
        };
        public selectedCampaignColumn: any;
        public selectedDataType: string;
        public fields: Field[];
        private cancelImporting = false;
        private permissionProvider: PermissionCheck;

        constructor(
            $scope: any,
            $routeParams: any,
            VoterFileDataProvider: VoterFileDataProvider,
            ToastUtils: ToastUtils,
            FieldDataProvider: FieldDataProvider,
            $window: any,
            ElectionDataProvider: ElectionDataProvider,
            PermissionCheck: PermissionCheck,
            __env: any,
            __app_type: string,
            DialogUtils: DialogUtils,
            Utils: Utils,
            ScreenUtils: ScreenUtils,
            NavigationBarHelperService: NavigationBarHelperService
        ) {
            this.$scope = $scope;
            this.$routeParams = $routeParams;
            this.dataProvider = VoterFileDataProvider;
            this.toast = ToastUtils;
            this.fieldDataProvider = FieldDataProvider;
            this.$window = $window;
            this.electionDataProvider = ElectionDataProvider;
            this.permissionProvider = PermissionCheck;
            this.__env = __env;
            this.__app_type = __app_type;
            this.dialog = DialogUtils;
            this.utils = Utils;
            this.screen = ScreenUtils;

            this.voterFileString = this.__env.strings.voter_file;
            this.isLeaderView = __app_type === "LEADER";
            const requestCallback = (voterFile: VoterFileImport) => {
                if (this.cancelImporting) return;
                this.register = voterFile;

                voterFile.getFileData().then(
                    () => {
                        this.dataProvider.getFields().then(() => {
                            if (this.cancelImporting) return;
                            this.openChooseImportTypeDialog(true, "all");
                            this.validVoterFile = true;
                        });
                    },
                    () => {
                        if (this.cancelImporting) return;

                        this.validVoterFile = false;
                        this.error =
                            "Something went wrong. Check that your file is of the correct type (xlsx, xls or csv) and not password protected.";
                        this.toast.error();
                    }
                );
            };
            const errorCallback = () => {
                if (this.cancelImporting) return;
                this.toast.error();
            };

            if (this.$routeParams.imid) {
                this.dataProvider
                    .importInfo(this.$routeParams.imid)
                    .then(requestCallback, errorCallback);
            } else {
                this.dataProvider
                    .info(this.$routeParams.voterFileId)
                    .then(requestCallback, errorCallback);
            }

            this.fieldDataProvider.getAllFields().then((fields: Field[]) => {
                this.fields = fields;
            });

            NavigationBarHelperService.setShowBackButton(true);
        }

        clickBack(): void {
            this.cancelImporting = true;
            this.utils.navigateBack();
        }

        showResetDialog(ev: any): void {
            this.dialog
                .delete({
                    targetEvent: ev,
                    skipHide: true,
                    scope: this.$scope,
                    locals: {
                        bodyText: "This action will be permanent",
                        title: `Reset column mappings?`,
                    },
                })
                .then(() => {
                    this.reset();
                });
        }

        reset(): void {
            this.populateDefaultRegisterColumns(this.register.data);
        }

        openChooseCampaignColumnDialog(
            erid: string | number,
            columns: object[],
            firstTime: boolean,
            selectedColumn?: object
        ): void {
            this.dialog
                .show({
                    component: "vc-choose-campaign-column-dialog",
                    skipHide: true,
                    clickOutsideToClose: false,
                    escapeToClose: false,
                    scope: this.$scope,
                    locals: {
                        erid: erid,
                        columns: columns,
                        firstTime: firstTime,
                        selectedColumn: selectedColumn,
                    },
                    ariaLabel: "Select Account Column",
                })
                .then((selectedCampaignColumn: object) => {
                    this.selectedCampaignColumn = selectedCampaignColumn;

                    if (firstTime && this.matchedCount > 0) {
                        this.showGuessColumnsToast(this.matchedCount);
                    }
                });
        }

        /** True once the column is mapped to a field, election or custom field. */
        isColumnMapped(column: any): boolean {
            return !!(column && (column.columnid || column.elid || column.cfid));
        }

        /**
         * Clears one column's mapping. The field picker already unmaps when the
         * mapped field is clicked a second time; this exposes the same reset on
         * the row so it can be undone without reopening the dialog and hunting
         * for the highlighted entry. defaultColumn only nulls the mapping keys,
         * so icvalue and the sample data survive.
         */
        clearColumnMapping(column: any, ev: any): void {
            if (ev) {
                ev.stopPropagation();
            }
            angular.extend(column, this.defaultColumn);
        }

        showFieldPicker(column: object, ev: any): void {
            this.dialog.show({
                component: "vc-field-picker",
                targetEvent: ev,
                scope: this.$scope,
                locals: {
                    column: column,
                    defaultColumn: this.defaultColumn,
                    registerColumns: this.registerColumns,
                    importDataType: this.selectedDataType,
                },
                size: "x-large",
            });
        }

        populateDefaultRegisterColumns(registerData: any): void {
            for (let i = 0; i < registerData.length; i++) {
                if (
                    !this.selectedCampaignColumn ||
                    this.registerColumns[i].icvalue !==
                        this.selectedCampaignColumn.icvalue
                ) {
                    this.registerColumns[i] = _.cloneDeep(this.defaultColumn);
                    this.registerColumns[i].icvalue =
                        this.utils.getNameFromNumber(i);
                    this.registerColumns[i].data = registerData[i];
                }
            }
        }

        mapColumns(registerData: any, mappingData: any): void {
            this.populateDefaultRegisterColumns(registerData);
            for (let i = 0; i < this.registerColumns.length; i++) {
                _.merge(
                    this.registerColumns[i],
                    this.getMappingData(
                        mappingData,
                        this.registerColumns[i].icvalue
                    )
                );
            }
        }

        getMappingData(mappingData: any, icvalue: string | null): any {
            for (let i = 0; i < mappingData.length; i++) {
                if (icvalue && mappingData[i].icvalue == icvalue) {
                    return mappingData[i];
                }
            }
            return {};
        }

        guessColumns(registerData: any): void {
            let customfields: Field[], elections: object[];
            this.fieldDataProvider.getCustomFields().then((fields: Field[]) => {
                customfields = fields;
                this.electionDataProvider
                    .getAllElections()
                    .then((elec: object[]) => {
                        elections = elec;

                        for (let i = 0; i < registerData.length; i++) {
                            let guessedColumn =
                                this.dataProvider.columnForAlias(
                                    registerData[i][0],
                                    customfields,
                                    elections,
                                    this.selectedDataType
                                );
                            if (guessedColumn) {
                                let duplicate = false;
                                for (
                                    let j = 0;
                                    j < this.registerColumns.length;
                                    j++
                                ) {
                                    if (guessedColumn.columnid != null) {
                                        if (
                                            guessedColumn.columnid ==
                                            this.registerColumns[j].columnid
                                        ) {
                                            duplicate = true;
                                        }
                                    } else if (guessedColumn.cfid != null) {
                                        if (
                                            guessedColumn.cfid ==
                                            this.registerColumns[j].cfid
                                        ) {
                                            duplicate = true;
                                        }
                                    } else if (guessedColumn.elid != null) {
                                        if (
                                            guessedColumn.elid ==
                                            this.registerColumns[j].elid
                                        ) {
                                            duplicate = true;
                                        }
                                    }
                                }
                                if (!duplicate) {
                                    angular.extend(
                                        this.registerColumns[i],
                                        guessedColumn
                                    );
                                    this.matchedCount++;
                                }
                            }
                        }
                        if (!this.isLeaderView && this.matchedCount > 0) {
                            this.showGuessColumnsToast(this.matchedCount);
                        }
                    });
            });
        }

        showGuessColumnsToast(matchedCount: number): void {
            this.toast
                .action("Matched " + matchedCount + " fields", "UNDO")
                .then((response: string) => {
                    if (response === "ok") {
                        this.reset();
                    }
                });
        }

        columnDisplayName(column: any): string | null {
            return this.fieldDataProvider.columnDisplayName(column);
        }

        columnDisplayingData(column: any): boolean {
            for (let i = 1; i < column.data.length; i++) {
                if (column.data[i] != null) {
                    return true;
                }
            }
            return false;
        }

        columnWithID(columnid: string | number): any {
            let obj = this.registerColumns.filter((obj: any) => {
                if (obj.columnid == columnid) {
                    return obj;
                }
            });
            if (obj.length) {
                return obj[0];
            }
            return null;
        }

        populatedColumns(): object[] {
            return this.registerColumns.filter((obj: any) => {
                return (
                    (!angular.isUndefined(obj.columnid) && obj.columnid) ||
                    (!angular.isUndefined(obj.elid) && obj.elid) ||
                    (!angular.isUndefined(obj.cfid) && obj.cfid)
                );
            });
        }

        validateColumn(columnid: string | number): boolean {
            return this.columnWithID(columnid) !== null;
        }

        isValidImport(): boolean {
            this.error = null;
            let errors = [];
            if (this.selectedDataType != "houses") {
                if (!this.validateColumn(1)) {
                    errors.push(" First Name");
                }
                if (!this.validateColumn(2)) {
                    errors.push(" Surname");
                }
            }
            if (this.selectedDataType != "contacts") {
                if (!this.validateColumn(6)) {
                    errors.push(" Street Name");
                }
                if (!this.validateColumn(7)) {
                    errors.push(" Town/City");
                }
                if (!this.validateColumn(8)) {
                    errors.push(" County/State");
                }
                if (this.validateColumn(25) && !this.validateColumn(26)) {
                    errors.push(
                        " the Latitude column if you have selected the Longitude column"
                    );
                }
                if (!this.validateColumn(25) && this.validateColumn(26)) {
                    errors.push(
                        " the Longitude column if you have selected the Latitude column"
                    );
                }
            }
            if (errors.length > 0) {
                this.error =
                    " Import can’t start — you're missing required fields: " +
                    errors.join(",") +
                    ". Please complete the field mapping before continuing. ";
            }
            return errors.length === 0;
        }

        importRegister(ev: any): void {
            if (this.isValidImport()) {
                const populatedColumns = this.populatedColumns();
                this.dialog
                    .show({
                        component: "vc-confirm-import",
                        targetEvent: ev,
                        scope: this.$scope,
                        size: "large",
                        locals: {
                            registerColumns: populatedColumns,
                            register: this.register,
                            campaignColumn: this.selectedCampaignColumn,
                            imname: this.register.name(),
                            importDataType: this.selectedDataType,
                        },
                    })
                    .then(() => {
                        if (this.isLeaderView) {
                            this.$window.location =
                                "./leader.php/#/leader_voterfiles";
                        } else {
                            this.$window.location = "./#/voterfiles";
                        }
                    });
            }
        }

        editImportName($event?: any): void {
            const parentEl = angular.element(document.body);
            this.dialog
                .show({
                    parent: parentEl,
                    targetEvent: $event,
                    component: "vc-edit-import-name",
                    locals: {
                        import: this.register,
                        backendUpdate: false,
                    },
                    clickOutsideToClose: true,
                })
                .then((imp: VoterFileImport) => {
                    this.register = imp;
                });
        }

        openChooseImportTypeDialog(
            firstTime: boolean,
            selectedType: string
        ): void {
            let voterFile = this.register;
            this.dialog
                .show({
                    component: "vc-choose-import-data-type-dialog",
                    skipHide: true,
                    clickOutsideToClose: !firstTime,
                    escapeToClose: !firstTime,
                    scope: this.$scope,
                    size: "x-large",
                    locals: {
                        firstTime,
                        selectedType,
                    },
                    ariaLabel: "Select Import Data Type",
                })
                .then((selectedDataType: any) => {
                    this.selectedDataType = selectedDataType;
                    if (this.isLeaderView && firstTime) {
                        this.openChooseCampaignColumnDialog(
                            this.register.erid,
                            this.registerColumns,
                            true
                        );
                    }

                    if (voterFile.imid && firstTime) {
                        voterFile.getMapping().then(
                            () => {
                                this.mapColumns(
                                    voterFile.data,
                                    voterFile.mapping
                                );
                            },
                            () => {}
                        );
                    } else {
                        this.populateDefaultRegisterColumns(voterFile.data);

                        this.guessColumns(voterFile.data);
                    }
                });
        }
    },
};

angular
    .module("voterFile")
    .component(VoterFileImportComponent.selector, VoterFileImportComponent);
